import {
  Camera,
  Grid3x3,
  Loader2,
  Mic,
  RotateCcw,
  Search,
  X,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { analyzePhotoEntriesPath, ingredientsPath } from "@/routes"

// ── Types ─────────────────────────────────────────────────────────────────────

interface Ingredient {
  id: number
  name: string
  category: string
}

interface Tag {
  id: number
  name: string
}

export interface MealSheetProps {
  open: boolean
  onClose: () => void
  onLog: (entry: {
    ingredients: string[]
    tag_ids: number[]
    notes?: string
    photo_signed_id?: string
  }) => void
}

// ── Config ────────────────────────────────────────────────────────────────────

type Method = "browse" | "photo" | "voice"

/** Map ingredient category → suggested tag name */
const CATEGORY_TAG_MAP: Record<string, string> = {
  dairy: "dairy",
  grain: "gluten",
}

/** User-friendly category display names */
const CATEGORY_DISPLAY: Record<string, string> = {
  fruit: "Fruit",
  vegetable: "Vegetables",
  meat: "Proteins",
  fish: "Fish & Seafood",
  grain: "Grains & Bread",
  dairy: "Dairy",
  legume: "Legumes",
  nut: "Nuts & Seeds",
  condiment: "Condiments",
  beverage: "Beverages",
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const METHOD_CONFIG: { id: Method; label: string; Icon: typeof Camera }[] = [
  { id: "browse", label: "Browse", Icon: Grid3x3 },
  { id: "photo", label: "Photo", Icon: Camera },
  { id: "voice", label: "Voice", Icon: Mic },
]

// ── Component ─────────────────────────────────────────────────────────────────

export function MealSheet({ open, onClose, onLog }: MealSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cacheRef = useRef<{
    ingredientsByCategory: Record<string, Ingredient[]>
    availableTags: Tag[]
  } | null>(null)

  const [activeMethod, setActiveMethod] = useState<Method>("browse")
  const [ingredientsByCategory, setIngredientsByCategory] = useState<
    Record<string, Ingredient[]>
  >({})
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedIngredients, setSelectedIngredients] = useState<Set<string>>(
    new Set(),
  )
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set())
  const [notes, setNotes] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [loading, setLoading] = useState(false)

  // ── Photo state ───────────────────────────────────────────────────────────

  const [photoAnalyzing, setPhotoAnalyzing] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoSignedId, setPhotoSignedId] = useState<string | null>(null)
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null)

  // ── Form reset ────────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setActiveMethod("browse")
    setSelectedIngredients(new Set())
    setSelectedTagIds(new Set())
    setNotes("")
    setSearchQuery("")
    setPhotoAnalyzing(false)
    setPhotoError(null)
    setPhotoSignedId(null)
    setPhotoPreviewUrl(null)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) handleClose()
    },
    [handleClose],
  )

  // ── Keyboard handling ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, handleClose])

  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  // ── Data fetching ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return
    if (cacheRef.current) {
      setIngredientsByCategory(cacheRef.current.ingredientsByCategory)
      setAvailableTags(cacheRef.current.availableTags)
      return
    }
    setLoading(true)
    fetch(ingredientsPath({ format: "json" }), {
      headers: {
        Accept: "application/json",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      credentials: "same-origin",
    })
      .then(
        (res) =>
          res.json() as Promise<{
            ingredients: Record<string, Ingredient[]>
            tags: Tag[]
          }>,
      )
      .then((data) => {
        cacheRef.current = {
          ingredientsByCategory: data.ingredients,
          availableTags: data.tags,
        }
        setIngredientsByCategory(data.ingredients)
        setAvailableTags(data.tags)
      })
      .catch(() => {
        // Silently handle fetch errors — grid will show "No ingredients available"
      })
      .finally(() => setLoading(false))
  }, [open])

  // ── Ingredient toggle (with auto-tag-suggestion) ─────────────────────────

  const toggleIngredient = useCallback(
    (name: string) => {
      const isAdding = !selectedIngredients.has(name)

      setSelectedIngredients((prev) => {
        const next = new Set(prev)
        if (next.has(name)) {
          next.delete(name)
        } else {
          next.add(name)
        }
        return next
      })

      // Auto-suggest a tag when adding an ingredient (additive only)
      if (isAdding) {
        const allIngredients = Object.entries(ingredientsByCategory).flatMap(
          ([cat, ings]) => ings.map((i) => ({ ...i, category: cat })),
        )
        const found = allIngredients.find((i) => i.name === name)
        if (found) {
          const tagName = CATEGORY_TAG_MAP[found.category]
          if (tagName) {
            const matchingTag = availableTags.find((t) => t.name === tagName)
            if (matchingTag) {
              setSelectedTagIds((prev) => new Set([...prev, matchingTag.id]))
            }
          }
        }
      }
    },
    [selectedIngredients, ingredientsByCategory, availableTags],
  )

  // ── Tag toggle ────────────────────────────────────────────────────────────

  const toggleTag = useCallback((id: number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // ── Log handler ───────────────────────────────────────────────────────────

  const handleLog = useCallback(() => {
    if (selectedIngredients.size === 0) return
    const entry = {
      ingredients: [...selectedIngredients],
      tag_ids: [...selectedTagIds],
      notes: notes.trim() || undefined,
      photo_signed_id: photoSignedId ?? undefined,
    }
    resetForm()
    onLog(entry)
  }, [
    selectedIngredients,
    selectedTagIds,
    notes,
    photoSignedId,
    onLog,
    resetForm,
  ])

  const canLog = selectedIngredients.size > 0

  // ── Photo analysis ────────────────────────────────────────────────────────

  const handlePhotoFile = useCallback(async (file: File) => {
    setPhotoAnalyzing(true)
    setPhotoError(null)
    setPhotoSignedId(null)
    setPhotoPreviewUrl(URL.createObjectURL(file))

    const formData = new FormData()
    formData.append("photo", file)

    try {
      const res = await fetch(analyzePhotoEntriesPath(), {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content ?? "",
          "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })

      const data = (await res.json()) as {
        ingredients?: string[]
        photo_signed_id?: string
        error?: string
      }

      if (!res.ok || data.error) {
        setPhotoError(
          "Couldn't analyze photo. You can still browse for ingredients.",
        )
        if (data.photo_signed_id) setPhotoSignedId(data.photo_signed_id)
      } else {
        if (data.photo_signed_id) setPhotoSignedId(data.photo_signed_id)
        if (data.ingredients && data.ingredients.length > 0) {
          // Add each AI ingredient to selected set
          data.ingredients.forEach((name) => {
            const normalizedName = name.toLowerCase()
            setSelectedIngredients((prev) => new Set([...prev, normalizedName]))
          })

          // Auto-suggest tags for matched known ingredients
          // We look through all known categories for any category-matched items
          // This is done by checking category from the cached ingredient list
          const cachedIngredients = Object.entries(
            cacheRef.current?.ingredientsByCategory ?? {},
          ).flatMap(([cat, ings]) => ings.map((i) => ({ ...i, category: cat })))

          const cachedTags = cacheRef.current?.availableTags ?? []

          data.ingredients.forEach((name) => {
            const normalizedName = name.toLowerCase()
            const found = cachedIngredients.find(
              (i) => i.name.toLowerCase() === normalizedName,
            )
            if (found) {
              const tagName = CATEGORY_TAG_MAP[found.category]
              if (tagName) {
                const matchingTag = cachedTags.find((t) => t.name === tagName)
                if (matchingTag) {
                  setSelectedTagIds(
                    (prev) => new Set([...prev, matchingTag.id]),
                  )
                }
              }
            }
          })
        }
      }
    } catch {
      setPhotoError(
        "Couldn't analyze photo. You can still browse for ingredients.",
      )
    } finally {
      setPhotoAnalyzing(false)
    }
  }, [])

  const handlePhotoInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) void handlePhotoFile(file)
      // Reset input so same file can be re-selected
      e.target.value = ""
    },
    [handlePhotoFile],
  )

  const resetPhotoState = useCallback(() => {
    setPhotoAnalyzing(false)
    setPhotoError(null)
    setPhotoSignedId(null)
    setPhotoPreviewUrl(null)
  }, [])

  // ── Filtered ingredients ──────────────────────────────────────────────────

  const filteredIngredientsByCategory: Record<string, Ingredient[]> =
    searchQuery.trim()
      ? Object.fromEntries(
          Object.entries(ingredientsByCategory)
            .map(([cat, ings]): [string, Ingredient[]] => [
              cat,
              ings.filter((i) =>
                i.name.toLowerCase().includes(searchQuery.toLowerCase()),
              ),
            ])
            .filter(([, ings]) => ings.length > 0),
        )
      : ingredientsByCategory

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={overlayRef}
      className={`meal-sheet-overlay fixed inset-0 z-30 ${open ? "open" : ""}`}
      style={{ background: "#00000040", overscrollBehavior: "contain" }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="meal-sheet-title"
    >
      {/* Hidden file inputs for photo capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Take photo"
        style={{ display: "none" }}
        onChange={handlePhotoInputChange}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        aria-label="Upload photo"
        style={{ display: "none" }}
        onChange={handlePhotoInputChange}
      />

      <div
        className="meal-sheet-panel fixed right-0 bottom-0 left-0 flex flex-col"
        style={{
          background: "var(--fig-action-sheet-bg)",
          borderRadius: "20px 20px 0 0",
          boxShadow: "var(--fig-action-sheet-shadow)",
          maxHeight: "88dvh",
        }}
      >
        {/* Handle + Header */}
        <div className="shrink-0 px-5 pt-3 pb-2">
          <div className="mb-3 flex justify-center">
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "var(--fig-divider-color)",
              }}
            />
          </div>
          <div className="flex items-center justify-between">
            <h2 id="meal-sheet-title" style={{ fontSize: 18, fontWeight: 700 }}>
              Log Meal
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={handleClose}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                color: "var(--fig-text-inactive)",
              }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Method tabs */}
        <div className="shrink-0 px-5 pt-1 pb-3">
          <div className="flex gap-2">
            {METHOD_CONFIG.map(({ id, label, Icon }) => {
              const isActive = activeMethod === id
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveMethod(id)}
                  aria-pressed={isActive}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 16px",
                    borderRadius: 12,
                    background: isActive
                      ? "var(--fig-method-active-bg)"
                      : "var(--fig-method-inactive-bg)",
                    color: isActive
                      ? "var(--fig-text-active)"
                      : "var(--fig-method-inactive-color)",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    transition: "background 150ms ease, color 150ms ease",
                  }}
                >
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Selected ingredient chips */}
        {selectedIngredients.size > 0 && (
          <div className="shrink-0 px-5 pb-3">
            <div className="flex flex-wrap gap-1.5">
              {[...selectedIngredients].map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleIngredient(name)}
                  aria-label={`Remove ${capitalize(name)}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 9999,
                    background: "var(--fig-chip-selected-bg)",
                    border: "1.5px solid var(--fig-chip-selected-border)",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--fig-text-primary)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "background 120ms ease",
                  }}
                >
                  {capitalize(name)}
                  <X
                    size={14}
                    style={{ color: "var(--fig-chip-remove)" }}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Panel container — fixed height so sheet doesn't jump between methods */}
        <div
          className="shrink-0"
          style={{ position: "relative", height: 320, overflow: "hidden" }}
        >
          {/* ── Browse panel ── */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              opacity: activeMethod === "browse" ? 1 : 0,
              pointerEvents: activeMethod === "browse" ? "auto" : "none",
              transition: "opacity 180ms ease",
              overflow: "hidden",
            }}
          >
            {/* Search */}
            <div className="shrink-0 px-5 pb-3">
              <div style={{ position: "relative" }}>
                <Search
                  size={16}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--fig-text-placeholder)",
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Search ingredients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search ingredients"
                  style={{
                    width: "100%",
                    height: 40,
                    padding: "0 12px 0 36px",
                    borderRadius: 12,
                    border: "1.5px solid var(--fig-input-border)",
                    background: "var(--fig-input-bg)",
                    fontFamily: "inherit",
                    fontSize: 14,
                    color: "var(--fig-text-primary)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor =
                      "var(--fig-input-focus-border)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--fig-input-border)")
                  }
                />
              </div>
            </div>

            {/* Scrollable ingredient grid */}
            <div
              style={{
                flex: "1 1 0",
                overflow: "auto",
                padding: "0 20px 12px",
              }}
            >
              {loading ? (
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--fig-text-placeholder)",
                    textAlign: "center",
                    paddingTop: 24,
                  }}
                >
                  Loading...
                </p>
              ) : Object.keys(filteredIngredientsByCategory).length === 0 ? (
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--fig-text-placeholder)",
                    textAlign: "center",
                    paddingTop: 24,
                  }}
                >
                  {searchQuery
                    ? "No ingredients found"
                    : "No ingredients available"}
                </p>
              ) : (
                Object.entries(filteredIngredientsByCategory).map(
                  ([category, ingredients]) => (
                    <div key={category} style={{ marginBottom: 16 }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--fig-category-label)",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        {CATEGORY_DISPLAY[category] ?? capitalize(category)}
                      </span>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        {ingredients.map((ingredient) => {
                          const isSelected = selectedIngredients.has(
                            ingredient.name,
                          )
                          return (
                            <button
                              key={ingredient.id}
                              type="button"
                              onClick={() => toggleIngredient(ingredient.name)}
                              aria-pressed={isSelected}
                              aria-label={capitalize(ingredient.name)}
                              className={`meal-grid-item flex flex-col items-center gap-1 p-3 ${isSelected ? "selected" : ""}`}
                              style={{
                                background: isSelected
                                  ? "var(--fig-grid-item-selected-bg)"
                                  : "var(--fig-grid-item-bg)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: "var(--fig-text-primary)",
                                  textAlign: "center",
                                  wordBreak: "break-word",
                                }}
                              >
                                {capitalize(ingredient.name)}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>
          </div>

          {/* ── Photo panel ── */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              opacity: activeMethod === "photo" ? 1 : 0,
              pointerEvents: activeMethod === "photo" ? "auto" : "none",
              transition: "opacity 180ms ease",
              padding: "20px",
            }}
          >
            {photoAnalyzing ? (
              /* Analyzing state */
              <div style={{ textAlign: "center" }}>
                <Loader2
                  size={36}
                  style={{
                    color: "var(--fig-text-inactive)",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 12px",
                  }}
                  aria-hidden="true"
                />
                <p
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "var(--fig-text-primary)",
                  }}
                >
                  Analyzing your meal...
                </p>
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--fig-text-inactive)",
                    marginTop: 6,
                  }}
                >
                  Identifying ingredients
                </p>
              </div>
            ) : photoError ? (
              /* Error state */
              <div style={{ textAlign: "center", maxWidth: 280 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "var(--fig-grid-item-bg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <Camera
                    size={24}
                    style={{ color: "var(--fig-text-inactive)" }}
                    aria-hidden="true"
                  />
                </div>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--fig-text-primary)",
                    marginBottom: 6,
                  }}
                >
                  {photoError}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--fig-text-inactive)",
                    marginBottom: 16,
                  }}
                >
                  You can also use Browse to add ingredients
                </p>
                <button
                  type="button"
                  onClick={resetPhotoState}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 10,
                    background: "var(--fig-grid-item-bg)",
                    border: "1.5px solid var(--fig-input-border)",
                    color: "var(--fig-text-primary)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : photoSignedId && photoPreviewUrl ? (
              /* Results state */
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginBottom: 12,
                  }}
                >
                  <img
                    src={photoPreviewUrl}
                    alt="Meal photo"
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 16,
                      objectFit: "cover",
                    }}
                  />
                  <button
                    type="button"
                    onClick={resetPhotoState}
                    aria-label="Retake photo"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "var(--fig-action-sheet-bg)",
                      border: "1.5px solid var(--fig-input-border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <RotateCcw
                      size={14}
                      style={{ color: "var(--fig-text-inactive)" }}
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--fig-text-primary)",
                  }}
                >
                  Photo analyzed
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--fig-text-inactive)",
                    marginTop: 4,
                  }}
                >
                  {selectedIngredients.size} ingredient
                  {selectedIngredients.size === 1 ? "" : "s"} found
                </p>
              </div>
            ) : (
              /* Capture state */
              <div style={{ textAlign: "center", width: "100%" }}>
                <div
                  style={{
                    border: "2px dashed var(--fig-divider-color)",
                    borderRadius: 16,
                    padding: "24px 20px",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      background: "var(--fig-grid-item-bg)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <Camera
                      size={24}
                      style={{ color: "var(--fig-text-inactive)" }}
                      aria-hidden="true"
                    />
                  </div>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--fig-text-primary)",
                      marginBottom: 6,
                    }}
                  >
                    Snap your meal
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--fig-text-inactive)",
                      lineHeight: 1.4,
                    }}
                  >
                    AI will identify ingredients
                  </p>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 12,
                      background: "var(--fig-method-active-bg)",
                      color: "var(--fig-text-active)",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <Camera size={16} aria-hidden="true" />
                    Take Photo
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 12,
                      background: "var(--fig-grid-item-bg)",
                      color: "var(--fig-text-primary)",
                      border: "1.5px solid var(--fig-input-border)",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "inherit",
                    }}
                  >
                    Upload
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Voice panel (coming soon) ── */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              opacity: activeMethod === "voice" ? 1 : 0,
              pointerEvents: activeMethod === "voice" ? "auto" : "none",
              transition: "opacity 180ms ease",
              padding: "20px",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: "var(--fig-grid-item-bg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mic
                size={28}
                style={{ color: "var(--fig-text-inactive)" }}
                aria-hidden="true"
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--fig-text-primary)",
                }}
              >
                Voice input
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--fig-text-inactive)",
                  marginTop: 6,
                  lineHeight: 1.4,
                }}
              >
                Coming soon — describe your meal and AI will extract the
                ingredients
              </p>
            </div>
          </div>
        </div>

        {/* Tags + Notes + Log button */}
        <div
          className="shrink-0 px-5 pt-3 pb-6"
          style={{ borderTop: "1px solid var(--fig-card-border)" }}
        >
          {/* Tags */}
          {availableTags.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {availableTags
                  .filter((t) => selectedTagIds.has(t.id))
                  .map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      aria-label={`Remove tag ${tag.name}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "4px 10px",
                        borderRadius: 8,
                        background: "var(--fig-tag-bg)",
                        color: "var(--fig-tag-text)",
                        fontSize: 12,
                        fontWeight: 600,
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {tag.name}
                      <X size={10} aria-hidden="true" />
                    </button>
                  ))}
                {availableTags
                  .filter((t) => !selectedTagIds.has(t.id))
                  .map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      aria-label={`Add tag ${tag.name}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 10px",
                        borderRadius: 8,
                        background: "var(--fig-action-item-hover)",
                        color: "var(--fig-text-inactive)",
                        fontSize: 12,
                        fontWeight: 500,
                        border: "1px dashed var(--fig-divider-color)",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      + {tag.name}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: 12 }}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note (optional)"
              rows={2}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1.5px solid var(--fig-input-border)",
                background: "var(--fig-input-bg)",
                fontFamily: "inherit",
                fontSize: 14,
                color: "var(--fig-text-primary)",
                resize: "none",
                outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = "var(--fig-input-focus-border)")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = "var(--fig-input-border)")
              }
            />
          </div>

          {/* Log button */}
          <button
            type="button"
            onClick={handleLog}
            disabled={!canLog}
            style={{
              width: "100%",
              height: 48,
              borderRadius: 14,
              background: canLog
                ? "var(--fig-log-btn-bg)"
                : "var(--fig-action-item-hover)",
              boxShadow: canLog ? "var(--fig-log-btn-shadow)" : "none",
              color: canLog
                ? "var(--fig-text-active)"
                : "var(--fig-text-placeholder)",
              border: "none",
              cursor: canLog ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              opacity: canLog ? 1 : 0.6,
              transition: "opacity 150ms ease, background 150ms ease",
            }}
          >
            Log Meal
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
